import { formatDistanceToNow } from "date-fns";
import { CopyButton } from "@/components/animate-ui/components/buttons/copy";
import { CircleCheck } from "@/components/animate-ui/icons/circle-check";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import type { components } from "@/lib/api/types";

type QuickCreateResponse = components["schemas"]["QuickCreateTaskResponse"];

interface TaskCreationHeroProps {
	isAuthenticated: boolean;
	heading: string;
}

export function TaskCreationHero({ isAuthenticated, heading }: TaskCreationHeroProps) {
	if (isAuthenticated) {
		return (
			<div className="space-y-2">
				<h1 className="text-4xl font-semibold tracking-tight lg:text-5xl">{heading}</h1>
			</div>
		);
	}

	return (
		<div className="space-y-4 mb-8">
			<h1 className="text-4xl font-semibold tracking-tight lg:text-5xl">
				AI teammates that actually do your work.
			</h1>
			<p className="text-xl text-muted-foreground">
				Answers questions. Gives advice. Goes off and does tasks for you.
			</p>
		</div>
	);
}

export function UnauthenticatedHowItWorks() {
	return (
		<div className="grid grid-cols-1 gap-6 sm:grid-cols-3 pt-12">
			<div className="space-y-1">
				<p className="text-sm font-medium">1. Connect your apps</p>
				<p className="text-sm text-muted-foreground">
					Give your teammate access to company knowledge and the tools to take action.
				</p>
			</div>
			<div className="space-y-1">
				<p className="text-sm font-medium">2. Delegate in plain English</p>
				<p className="text-sm text-muted-foreground">
					Describe tasks on a schedule, in the web app, or by sending an email.
				</p>
			</div>
			<div className="space-y-1">
				<p className="text-sm font-medium">3. Work gets done</p>
				<p className="text-sm text-muted-foreground">
					Your teammate executes autonomously and delivers real results.
				</p>
			</div>
		</div>
	);
}

interface ScheduledTaskSuccessDialogProps {
	successData: QuickCreateResponse | null;
	onOpenChange: (open: boolean) => void;
	onSeeTeammate: () => void;
	onRunNow: () => void;
	onCreateAnother: () => void;
}

export function ScheduledTaskSuccessDialog({
	successData,
	onOpenChange,
	onSeeTeammate,
	onRunNow,
	onCreateAnother,
}: ScheduledTaskSuccessDialogProps) {
	return (
		<Dialog open={!!successData} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-sm">
				<DialogHeader className="items-center text-center">
					<CircleCheck className="mb-2 size-10 text-accent-cyan" />
					<DialogTitle>Task scheduled</DialogTitle>
					<DialogDescription>
						{successData?.schedule?.next_run_time
							? `First run ${formatDistanceToNow(new Date(successData.schedule.next_run_time), { addSuffix: true }).replace("about ", "")}`
							: "Your task has been scheduled."}
					</DialogDescription>
				</DialogHeader>
				<DialogFooter className="flex-col gap-2 sm:flex-col">
					<Button onClick={onSeeTeammate}>See teammate</Button>
					<Button variant="outline" onClick={onRunNow}>
						Run now
					</Button>
					<Button variant="ghost" onClick={onCreateAnother}>
						Create another task
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

interface WebhookUrlDialogProps {
	webhookUrl: string | null;
	onOpenChange: (open: boolean) => void;
}

export function WebhookUrlDialog({ webhookUrl, onOpenChange }: WebhookUrlDialogProps) {
	return (
		<Dialog open={!!webhookUrl} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-xl">
				<DialogHeader>
					<DialogTitle>Webhook URL Created</DialogTitle>
					<DialogDescription>
						Make a POST request to this URL with an optional JSON payload to trigger this task. The
						full URL is shown only once. Copy it now.
					</DialogDescription>
				</DialogHeader>
				{webhookUrl && (
					<div className="rounded-md border bg-muted/40 p-3 flex items-start justify-between gap-3">
						<code className="text-xs break-all">{webhookUrl}</code>
						<CopyButton content={webhookUrl} variant="outline" size="sm" />
					</div>
				)}
				<DialogFooter>
					<Button type="button" onClick={() => onOpenChange(false)}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
